import { Link, Navigate, useOutletContext } from "react-router";
import { useState, useRef } from "react";
import { useAccountStore } from "../../stores/accountStore";
import { useChatFlagsStore } from "../../stores/chatFlagsStore";
import { createClient } from "../../lib/xmpp/factories";
import type { IDBPersistence, Persistence, RawClient } from "../../lib/xmpp";

// dev-only logger — strip all console noise from production builds
const dlog = (...args: unknown[]) => { if (import.meta.env.DEV) console.log(...args); };
const dwarn = (...args: unknown[]) => { if (import.meta.env.DEV) console.warn(...args); };

type AppContext = {
	accounts: Record<string, ReturnType<typeof newAccount>>;
	persistence: Persistence;
	idbp: IDBPersistence;
	initialSelectedAccount?: string;
};

/**
 * Attempt to subscribe this client to push notifications.
 * Safe to call multiple times — checks prerequisites first and logs every
 * decision so the exact failure point is visible in DevTools console.
 * Exported so NotificationBanner can call it immediately after permission grant.
 */
export const doSubscribePush = async (
	client: RawClient,
	idbp: IDBPersistence,
) => {
	dlog("[push] doSubscribePush called");

	if (!import.meta.env.VITE_PUSH_SERVICE) {
		// Hard blocker: build the Docker image with
		//   --build-arg VITE_PUSH_SERVICE=<snikket-push-url>
		// The URL is the Snikket server's push gateway, e.g. https://snikket.example.com
		dwarn("[push] VITE_PUSH_SERVICE not set at build time — push disabled. " +
			"Add --build-arg VITE_PUSH_SERVICE=<url> to the docker build step.");
		return;
	}

	if (!navigator.serviceWorker) {
		dwarn("[push] navigator.serviceWorker unavailable (not HTTPS or not PWA?)");
		return;
	}

	// Check permission — must compare string, NOT use !Notification.permission (always truthy)
	const perm = typeof Notification !== "undefined" ? Notification.permission : "denied";
	dlog("[push] Notification.permission =", perm);
	if (perm !== "granted") {
		dwarn("[push] permission not granted — skipping subscription");
		return;
	}

	dlog("[push] awaiting SW ready");
	const reg = await navigator.serviceWorker.ready;
	dlog("[push] SW ready, scope:", reg.scope, "state:", reg.active?.state);

	// Log existing browser-side push subscription (if any)
	const existing = await reg.pushManager.getSubscription().catch(() => null);
	dlog("[push] existing PushManager subscription:", existing?.endpoint ?? "none");

	dlog("[push] fetching/generating ECDSA key pair");
	let stored = (await (idbp as any).get("vapid_key")) as
		| { publicKey: ArrayBuffer; privateKey: ArrayBuffer }
		| undefined;
	let keyPair: CryptoKeyPair;
	if (stored) {
		dlog("[push] reusing stored key");
		keyPair = {
			publicKey: await window.crypto.subtle.importKey(
				"raw", stored.publicKey, { name: "ECDSA", namedCurve: "P-256" }, true, ["verify"],
			),
			privateKey: await window.crypto.subtle.importKey(
				"pkcs8", stored.privateKey, { name: "ECDSA", namedCurve: "P-256" }, true, ["sign"],
			),
		};
	} else {
		dlog("[push] generating new key pair");
		keyPair = await window.crypto.subtle.generateKey(
			{ name: "ECDSA", namedCurve: "P-256" }, true, ["sign", "verify"],
		);
		stored = {
			publicKey: await window.crypto.subtle.exportKey("raw", keyPair.publicKey),
			privateKey: await window.crypto.subtle.exportKey("pkcs8", keyPair.privateKey),
		};
		await (idbp as any).set("vapid_key", stored);
		dlog("[push] key pair stored in IDB");
	}

	const pushUrl = import.meta.env.VITE_PUSH_SERVICE;
	dlog("[push] calling client.subscribePush, service =", pushUrl);
	try {
		await (client as any).subscribePush(reg, pushUrl, keyPair, 144);
		dlog("[push] subscribePush() completed — subscription registered with server");
	} catch (err) {
		console.error("[push] subscribePush() threw:", err);
	}
};

const registerPush = (
	client: RawClient,
	idbp: IDBPersistence,
) => {
	// Attempt subscription whenever the client comes online.
	// Also call doSubscribePush() from NotificationBanner after permission is granted,
	// because the online event may have already fired before the user granted permission.
	client.addStatusOnlineListener(() => doSubscribePush(client, idbp));
};

export const newAccount = (
	accountId: string,
	idbp: IDBPersistence,
	persistence: Persistence,
) => {
	const client = createClient(accountId, persistence);
	registerPush(client, idbp);
	setTimeout(() => client.start(), 0);
	return { client, status: "connecting" as const };
};

type AccountStatus = "connecting" | "online" | "offline" | "error" | "auth";

const RegisterLink = () => {
	const registrationServer = useChatFlagsStore((s) => s.registrationServer);
	const setRegistrationServer = useChatFlagsStore((s) => s.setRegistrationServer);
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(registrationServer);

	if (editing) {
		return (
			<div className="space-y-2 text-center">
				<input
					value={draft}
					onChange={(e) => setDraft(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") { setRegistrationServer(draft.trim() || registrationServer); setEditing(false); }
						if (e.key === "Escape") { setEditing(false); setDraft(registrationServer); }
					}}
					placeholder="snikket.holm.chat"
					autoFocus
					className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/15 text-sm text-white placeholder-white/40 focus:outline-none focus:border-primary"
				/>
				<div className="flex gap-2 justify-center text-xs">
					<button
						type="button"
						onClick={() => { setRegistrationServer(draft.trim() || registrationServer); setEditing(false); }}
						className="px-3 py-1 rounded text-primary-light hover:underline"
					>Save</button>
					<button
						type="button"
						onClick={() => { setEditing(false); setDraft(registrationServer); }}
						className="px-3 py-1 rounded text-white/55 hover:text-white/80"
					>Cancel</button>
				</div>
			</div>
		);
	}

	return (
		<p className="text-center text-sm text-white/55 space-x-1">
			<Link
				to={`/register?with=${encodeURIComponent(registrationServer)}`}
				className="text-primary-light hover:underline"
				target="_self"
			>
				Create account on {registrationServer}
			</Link>
			<button
				type="button"
				onClick={() => setEditing(true)}
				className="text-white/40 hover:text-white/70 text-xs"
				title="Change registration server"
			>
				(change)
			</button>
		</p>
	);
};

export const Login = () => {
	const ctx = useOutletContext<AppContext>();
	const { persistence, idbp } = ctx;
	const [accountId, setAccountId] = useState<string | undefined>(undefined);
	const [authAttempts, setAuthAttempts] = useState(0);
	const [status, setStatus] = useState<AccountStatus>("connecting");
	const clientRef = useRef<RawClient | null>(null);

	if (status === "online" && accountId) {
		return (
			<Navigate to={`/accounts/${encodeURIComponent(accountId).replace(/%40/, "@")}`} />
		);
	}

	const startClient = (jid: string) => {
		// Create client and attach listeners BEFORE calling start()
		const client = createClient(jid, persistence);
		registerPush(client, idbp);

		client.addStatusOnlineListener(() => {
			useAccountStore.getState().addAccount(jid, { client, status: "online" });
			setStatus("online");
		});
		client.addStatusOfflineListener(() => setStatus((s) => (s === "auth" || s === "error") ? s : "connecting"));
		client.addConnectionFailedListener(() => setStatus("error"));
		client.addPasswordNeededListener(() => setStatus("auth"));

		clientRef.current = client;
		setTimeout(() => client.start(), 0);
	};

	const resetViewportZoom = () => {
		// iOS doesn't reset zoom after keyboard dismisses — do it manually
		const viewport = document.querySelector("meta[name=viewport]") as HTMLMetaElement | null;
		if (!viewport) return;
		const original = viewport.content;
		viewport.content = original.includes("maximum-scale")
			? original
			: original + ", maximum-scale=1";
		// Brief timeout lets the zoom-reset take effect, then restore
		requestAnimationFrame(() => { viewport.content = original; });
	};

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const form = e.currentTarget;
		// Blur focused input so iOS keyboard closes, then reset zoom
		(document.activeElement as HTMLElement | null)?.blur();
		resetViewportZoom();

		if (!accountId) {
			const jid = (form as any).jid.value as string;
			if (!jid) return;
			setAuthAttempts(0);
			setStatus("connecting");
			setAccountId(jid);
			startClient(jid);
		} else {
			setAuthAttempts((n) => n + 1);
			clientRef.current?.usePassword((form as any).password.value);
			setStatus("connecting");
		}
	};

	const connectError = status === "error";
	const needAuth = status === "auth";
	const isConnecting = !!accountId && !connectError && !needAuth;

	return (
		<div className="min-h-screen flex items-center justify-center p-4" style={{ paddingTop: "max(1rem, env(safe-area-inset-top))" }}>
			<div className="glass-panel rounded-2xl p-8 w-full max-w-sm shadow-2xl">
				<div className="text-center mb-8">
					<div className="w-16 h-16 rounded-2xl bg-primary mx-auto mb-4 flex items-center justify-center">
						<svg viewBox="0 0 24 24" fill="white" className="w-10 h-10">
							<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
						</svg>
					</div>
					<h1 className="text-2xl font-semibold text-white">Cheogram</h1>
					<p className="text-white/55 text-sm mt-1">Connect to all your contacts</p>
				</div>

				<form onSubmit={handleSubmit} className="space-y-3">
					<input
						name="jid"
						type="text"
						inputMode="email"
						autoComplete="username"
						autoCorrect="off"
						autoCapitalize="none"
						spellCheck={false}
						placeholder="Jabber ID (user@server)"
						defaultValue={accountId}
						disabled={isConnecting}
						className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white placeholder-white/40 text-base focus:outline-none focus:border-primary focus:bg-white/15 transition-all disabled:opacity-50"
					/>

					{connectError && (
						<div className="px-4 py-3 rounded-xl bg-red-500/20 border border-red-500/30 text-red-300 text-sm space-y-1">
							<p className="font-medium">Couldn't reach the XMPP server.</p>
							<p className="text-xs text-red-300/80">
								Check that your Jabber ID is correct, that the server is online, and that
								it serves <code>/.well-known/host-meta</code> with valid alt-connection
								endpoints.
							</p>
						</div>
					)}

					{needAuth && (
						<input
							name="password"
							type="password"
							placeholder="Password"
							autoFocus
							className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white placeholder-white/40 text-base focus:outline-none focus:border-primary focus:bg-white/15 transition-all"
						/>
					)}

					{needAuth && authAttempts > 0 && (
						<div className="px-4 py-3 rounded-xl bg-red-500/20 border border-red-500/30 text-red-300 text-sm">
							Login failed. Please try again.
						</div>
					)}

					{isConnecting ? (
						<div className="flex justify-center py-3">
							<div className="w-6 h-6 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
						</div>
					) : (
						<button
							type="submit"
							className="w-full py-3 rounded-xl bg-primary hover:bg-primary-light text-white font-medium text-sm transition-colors"
						>
							{needAuth ? "Login" : connectError ? "Retry" : "Next"}
						</button>
					)}

					{!accountId && <RegisterLink />}
				</form>
			</div>
		</div>
	);
};
